import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ArrowRight, Loader, CheckCircle } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { DEMO_ACCESS_MODAL } from '../constants';

// ─── Indicatifs téléphoniques internationaux ─────────────────────────────────
const COUNTRY_CODES = [
  { code: '+33',  flag: '🇫🇷', label: 'France' },
  { code: '+32',  flag: '🇧🇪', label: 'Belgique' },
  { code: '+41',  flag: '🇨🇭', label: 'Suisse' },
  { code: '+352', flag: '🇱🇺', label: 'Luxembourg' },
  { code: '+377', flag: '🇲🇨', label: 'Monaco' },
  { code: '+212', flag: '🇲🇦', label: 'Maroc' },
  { code: '+213', flag: '🇩🇿', label: 'Algérie' },
  { code: '+216', flag: '🇹🇳', label: 'Tunisie' },
  { code: '+221', flag: '🇸🇳', label: 'Sénégal' },
  { code: '+225', flag: '🇨🇮', label: "Côte d'Ivoire" },
  { code: '+1',   flag: '🇨🇦', label: 'Canada' },
  { code: '+44',  flag: '🇬🇧', label: 'Royaume-Uni' },
  { code: '+49',  flag: '🇩🇪', label: 'Allemagne' },
  { code: '+34',  flag: '🇪🇸', label: 'Espagne' },
  { code: '+39',  flag: '🇮🇹', label: 'Italie' },
  { code: '+351', flag: '🇵🇹', label: 'Portugal' },
  { code: '+31',  flag: '🇳🇱', label: 'Pays-Bas' },
  { code: '+1',   flag: '🇺🇸', label: 'États-Unis' },
];

// ─── Validation email ─────────────────────────────────────────────────────────
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
const isValidPhone = (v) => /^\d{6,}$/.test(v.replace(/[\s\-\(\)\.]/g, ''));

// ─── Composant ────────────────────────────────────────────────────────────────
export default function DemoRequestModal({ version, onClose }) {
  const { lang } = useLanguage();
  const t = DEMO_ACCESS_MODAL[lang];

  const [step, setStep] = useState(1);        // 1 = formulaire, 2 = OTP
  const [leadId, setLeadId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // ── Champs formulaire ──
  const [form, setForm] = useState({
    prenom: '', nom: '', email: '',
    indicatif: '+33', telephone: '', commentaire: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // ── OTP ──
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Fermer avec Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ─── Validation formulaire ────────────────────────────────────────────────
  const validateForm = useCallback(() => {
    const errors = {};
    if (!form.prenom.trim()) errors.prenom = t.required;
    if (!form.nom.trim()) errors.nom = t.required;
    if (!form.email.trim()) errors.email = t.required;
    else if (!isValidEmail(form.email)) errors.email = t.emailInvalid;
    if (!form.telephone.trim()) errors.telephone = t.required;
    else if (!isValidPhone(form.telephone)) errors.telephone = t.phoneInvalid;
    return errors;
  }, [form, t]);

  // ─── Soumettre le formulaire (étape 1) ────────────────────────────────────
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/demo-requests/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, version, origin: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t.errorGeneric);
      setLeadId(data.lead_id);
      setStep(2);
    } catch (err) {
      setError(err.message || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  // ─── Saisie OTP ───────────────────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/, '').slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  // ─── Vérifier l'OTP (étape 2) ────────────────────────────────────────────
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { setError(t.otpIncomplete); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/demo-requests/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: leadId, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t.errorGeneric);

      // Connexion automatique au CRM démo
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setSuccess(true);

      // Redirection vers le dashboard admin du CRM
      setTimeout(() => {
        window.location.href = '/admin';
      }, 1200);
    } catch (err) {
      setError(err.message || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  // ─── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div
      className="pb-demo-modal-overlay"
      data-testid="pb-demo-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="pb-demo-modal" data-testid="pb-demo-modal">

        {/* Bouton fermer */}
        <button
          className="pb-demo-modal-close"
          onClick={onClose}
          aria-label="Fermer"
          data-testid="pb-demo-modal-close"
        >
          <X size={18} />
        </button>

        {/* ── Succès ── */}
        {success && (
          <div className="pb-demo-modal-success" data-testid="pb-demo-modal-success">
            <CheckCircle size={48} className="pb-demo-modal-success-icon" />
            <h3>{t.successTitle}</h3>
            <p>{t.successText}</p>
          </div>
        )}

        {/* ── Étape 1 : Formulaire ── */}
        {!success && step === 1 && (
          <>
            <div className="pb-demo-modal-header">
              <div className="pb-label">{t.label}</div>
              <h2 className="pb-demo-modal-title">{t.title}</h2>
              <p className="pb-demo-modal-sub">{t.subtitle}</p>
            </div>

            <form onSubmit={handleFormSubmit} noValidate data-testid="pb-demo-form">
              {/* Prénom + Nom */}
              <div className="pb-demo-modal-row">
                <div className="pb-demo-modal-field">
                  <label>{t.prenom} <span>*</span></label>
                  <input
                    type="text"
                    placeholder={t.prenomPlaceholder}
                    value={form.prenom}
                    onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                    className={formErrors.prenom ? 'pb-input-error' : ''}
                    data-testid="pb-demo-field-prenom"
                  />
                  {formErrors.prenom && <span className="pb-demo-field-error">{formErrors.prenom}</span>}
                </div>
                <div className="pb-demo-modal-field">
                  <label>{t.nom} <span>*</span></label>
                  <input
                    type="text"
                    placeholder={t.nomPlaceholder}
                    value={form.nom}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    className={formErrors.nom ? 'pb-input-error' : ''}
                    data-testid="pb-demo-field-nom"
                  />
                  {formErrors.nom && <span className="pb-demo-field-error">{formErrors.nom}</span>}
                </div>
              </div>

              {/* Email */}
              <div className="pb-demo-modal-field">
                <label>{t.email} <span>*</span></label>
                <input
                  type="email"
                  placeholder={t.emailPlaceholder}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={formErrors.email ? 'pb-input-error' : ''}
                  data-testid="pb-demo-field-email"
                />
                {formErrors.email && <span className="pb-demo-field-error">{formErrors.email}</span>}
              </div>

              {/* Téléphone avec indicatif */}
              <div className="pb-demo-modal-field">
                <label>{t.telephone} <span>*</span></label>
                <div className="pb-demo-phone-row">
                  <select
                    value={form.indicatif}
                    onChange={(e) => setForm({ ...form, indicatif: e.target.value })}
                    className="pb-demo-phone-code"
                    data-testid="pb-demo-field-indicatif"
                  >
                    {COUNTRY_CODES.map((c, i) => (
                      <option key={i} value={c.code}>
                        {c.flag} {c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder={t.telephonePlaceholder}
                    value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                    className={`pb-demo-phone-input${formErrors.telephone ? ' pb-input-error' : ''}`}
                    data-testid="pb-demo-field-telephone"
                  />
                </div>
                {formErrors.telephone && <span className="pb-demo-field-error">{formErrors.telephone}</span>}
              </div>

              {/* Commentaire */}
              <div className="pb-demo-modal-field">
                <label>{t.commentaire}</label>
                <textarea
                  placeholder={t.commentairePlaceholder}
                  value={form.commentaire}
                  onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
                  rows={3}
                  data-testid="pb-demo-field-commentaire"
                />
              </div>

              {error && <p className="pb-demo-modal-error" data-testid="pb-demo-error">{error}</p>}

              <button
                type="submit"
                className="pb-btn-primary pb-demo-modal-submit"
                disabled={loading}
                data-testid="pb-demo-form-submit"
              >
                {loading ? <Loader size={16} className="pb-spin" /> : null}
                {loading ? t.sending : t.submit}
                {!loading && <ArrowRight size={16} />}
              </button>
            </form>
          </>
        )}

        {/* ── Étape 2 : Saisie OTP ── */}
        {!success && step === 2 && (
          <>
            <div className="pb-demo-modal-header">
              <div className="pb-label">{t.otpLabel}</div>
              <h2 className="pb-demo-modal-title">{t.otpTitle}</h2>
              <p className="pb-demo-modal-sub">
                {t.otpSubtitle} <strong>{form.email}</strong>
              </p>
            </div>

            <form onSubmit={handleOtpSubmit} data-testid="pb-demo-otp-form">
              <div className="pb-demo-otp-grid" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className={`pb-demo-otp-box${digit ? ' pb-demo-otp-filled' : ''}`}
                    data-testid={`pb-demo-otp-${i}`}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <p className="pb-demo-otp-hint">{t.otpHint}</p>

              {error && <p className="pb-demo-modal-error" data-testid="pb-demo-otp-error">{error}</p>}

              <button
                type="submit"
                className="pb-btn-primary pb-demo-modal-submit"
                disabled={loading || otp.join('').length < 6}
                data-testid="pb-demo-otp-submit"
              >
                {loading ? <Loader size={16} className="pb-spin" /> : null}
                {loading ? t.verifying : t.otpSubmit}
                {!loading && <ArrowRight size={16} />}
              </button>

              <button
                type="button"
                className="pb-demo-modal-back"
                onClick={() => { setStep(1); setOtp(['','','','','','']); setError(''); }}
                data-testid="pb-demo-otp-back"
              >
                {t.back}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
